import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Dropdown } from "react-bootstrap";
import { useQueryClient } from "react-query";
import TreeDetailTabs from "./TreeDetailTabs";
import {
  MapMarkerAltIcon,
  MenuAlt1Icon,
  LineChartIcon,
  MoreIcon,
  EditIcon,
} from "@components/icons";
import ExportSensor from "@modules/ExportSensor/ExportSensor";
import ServerError from "@components/Error/ServerError";
import PageNotFound from "@components/Error/PageNotFound";
import Spinner from "@components/Spinner/Spinner";
import {
  getTree,
  getPublicTree,
  getTreeImages,
  deleteTreeImage,
  uploadTree3DModel,
  removeTree3DModel,
} from "../services/treeCrud";
import { useMutation } from "react-query";
import TreeImageForm from "./Form/AddForm/TreeImageForm";
import TreeForm from "./Form/AddForm";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
import { hasPermission } from "@helper/permissions";
import "./TreeDetail.css";

export default function TreeDetail({
  aid,
  pid,
  treeId,
  type,
  setOpenTreeDetail,
  map,
  trees,
  onSensorEditStateChange,
  isPublic = false,
}) {
  const location = useLocation();
  const { tid: routeTreeId } = useParams();
  const query = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const tid = treeId || routeTreeId || query.get("selectedTree");
  const { t } = useTranslation();
  const [openForm, setOpenForm] = useState(false);
  const [treeImages, setTreeImages] = useState([]);
  const [leafImages, setLeafImages] = useState([]);
  const [treeIndex, setTreeIndex] = useState(0);
  const [leafIndex, setLeafIndex] = useState(0);
  const [openImageForm, setOpenImageForm] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [formInitialStep, setFormInitialStep] = useState(null);
  const [formIsolatedStep, setFormIsolatedStep] = useState(null);
  const { mobile } = useSelector((state) => state.mobile);
  const authUser = useSelector((state) => state.auth?.user);
  const canEditTree = !isPublic && hasPermission(authUser, "edit_tree");
  const normalizedRole = String(
    authUser?.role || authUser?.role_name || authUser?.account_role || authUser?.user_role || ""
  ).toLowerCase();
  const canManage3DModel =
    !isPublic && (normalizedRole === "superuser" || Number(authUser?.account_type) === 0);
  const [edit, setEdit] = useState(false);
  const [soilEdit, setSoilEdit] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [showData, setShowData] = useState(false);
  const [loadCareData, setLoadCareData] = useState(false);
  useEffect(() => {
    if (!openForm) {
      setFormInitialStep(null);
      setFormIsolatedStep(null);
    }
  }, [openForm]);

  let areaId = aid;
  const queryClient = useQueryClient();

  useEffect(() => {
    setLoadCareData(false);
    setTreeImages([]);
    setLeafImages([]);
    setTreeIndex(0);
    setLeafIndex(0);
  }, [tid]);

  // Delete image mutation
  const { mutateAsync: deleteImage } = useMutation(deleteTreeImage, {
    onSuccess: () => {
      queryClient.invalidateQueries(["treeimages", tid]);
    },
  });
  const { mutateAsync: upload3DModel, isLoading: isUploading3DModel } = useMutation(
    uploadTree3DModel,
    {
      onSuccess: (data) => {
        queryClient.setQueryData(["tree", tid], data);
        queryClient.invalidateQueries("trees");
      },
    }
  );
  const { mutateAsync: remove3DModel, isLoading: isRemoving3DModel } = useMutation(
    removeTree3DModel,
    {
      onSuccess: (data) => {
        queryClient.setQueryData(["tree", tid], data);
        queryClient.invalidateQueries("trees");
      },
    }
  );

  const handleDeleteImage = async (imageId, isLeafImage) => {
    if (window.confirm(t("confirm_delete_image"))) {
      await deleteImage(imageId);
      if (isLeafImage) {
        if (leafIndex >= leafImages.length - 1) {
          setLeafIndex(Math.max(0, leafIndex - 1));
        }
      } else {
        if (treeIndex >= treeImages.length - 1) {
          setTreeIndex(Math.max(0, treeIndex - 1));
        }
      }
    }
  };

  //retriving tree details with treeid
  const localTree = trees?.find((elem) => elem.id === +tid) ?? null;

  const scheduledGetTree = createScheduledQueryFn(
    () => (isPublic ? getPublicTree(tid) : getTree(tid)),
    REQUEST_PRIORITIES.HIGH,
    "getTree"
  );
  const {
    data: fetchedTree,
    isLoading,
    isError,
    error,
  } = useQuery([isPublic ? "publicTree" : "tree", tid], scheduledGetTree, {
    // Always fetch the full detail — the list uses a lightweight serializer without installations.
    // initialDataUpdatedAt: 0 forces React Query to treat the list item as immediately stale
    // so the detail fetch fires right away even though initialData is provided.
    enabled: !!tid,
    initialData: localTree || undefined,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 1000 * 60 * 60,
  });
  const tree = fetchedTree || localTree;
  if (aid == null) {
    // fetchedTree has nested area.id; localTree (lightweight) has flat area_id
    areaId = tree?.area?.id ?? tree?.area_id;
  }

  const scheduledGetTreeImages = createScheduledQueryFn(
    () => getTreeImages(tid),
    REQUEST_PRIORITIES.NORMAL,
    "getTreeImages"
  );
  useQuery(["treeimages", tid], scheduledGetTreeImages, {
    enabled: Boolean(tid) && loadCareData && !isPublic,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // delay between retries in milliseconds
    staleTime: 1000 * 60 * 60,
    onSuccess: (data) => {
      if (data?.treeimage_set.length !== 0) {
        const treeImages = data.treeimage_set?.filter((a) => a.tree_image);
        const leafImages = data.treeimage_set?.filter((a) => a.leaf_image);
        setTreeImages(treeImages);
        setTreeIndex(treeImages.length - 1);
        setLeafImages(leafImages);
        setLeafIndex(leafImages.length - 1);
      } else {
        const treeImages = [];
        const leafImages = [];
        setTreeImages(treeImages);
        setTreeIndex(0);
        setLeafImages(leafImages);
        setLeafIndex(0);
      }
    },
  });

  const handleAddSensor = () => {
    setFormInitialStep(null);
    setFormIsolatedStep("sensors");
    setIsUpdate(true);
    setOpenForm(true);
  };
  const handleEditTree = () => {
    const params = new URLSearchParams();
    params.set("selectedTree", String(selectedTreeId));
    params.set("editTree", "1");
    navigate(`/map?${params.toString()}`);
  };

  const selectedTreeId = tid || tree?.id;
  const is3DModelMutating = isUploading3DModel || isRemoving3DModel;

  const handleUpload3DModel = async (file) => {
    if (!file || !selectedTreeId) return;
    const form = new FormData();
    form.append("file", file);
    await upload3DModel({ tid: selectedTreeId, form });
  };

  const handleRemove3DModel = async () => {
    if (!selectedTreeId) return;
    if (!window.confirm(t("confirm_delete"))) return;
    await remove3DModel(selectedTreeId);
  };

  const activeInstallations = (Array.isArray(tree?.installations) ? tree.installations : []).filter(
    (i) => !i?.removed_at
  );
  const installationIds = activeInstallations.map((i) => i?.id).filter(Boolean);
  const sensors = activeInstallations.map((i) => i.sensor).filter(Boolean);

  if (isLoading) return <Spinner height={"73vh"} />;
  if (isError && error.response && error.response.status === 404) return <PageNotFound />;
  if (isError) return <ServerError />;

  const showHeaderActions = Boolean(tree);

  const handleClose = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedTree");
    const nextUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;

    if (type === "map") {
      setOpenTreeDetail?.(null);
      if (map && tree?.position) map.flyTo(tree.position, map.getZoom("18"));
      navigate(nextUrl, { replace: true });
    } else {
      navigate(nextUrl, { replace: true });
    }
  };

  const dsParams = new URLSearchParams();
  if (areaId) dsParams.set("areas", areaId);
  if (selectedTreeId) dsParams.set("trees", selectedTreeId);
  if (installationIds.length > 0) dsParams.set("installations", installationIds.join(","));
  const dsUrl = `/data-science${dsParams.toString() ? `?${dsParams.toString()}` : ""}`;

  const headerActions = tree ? (
    <div className="d-flex align-items-center gap-2 tree-detail-header-actions">
      <Dropdown align="end">
        <Dropdown.Toggle
          as="button"
          className="tree-detail-icon-link tree-detail-actions-toggle entity-detail-control entity-detail-actions-toggle border-0"
          id="tree-detail-actions-dropdown"
          title={t("more_actions", "Mehr Aktionen")}
        >
          <MoreIcon size="24px" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {canEditTree && (
            <>
              <Dropdown.Item onClick={handleEditTree}>
                <EditIcon size="16px" /> {t("edit", "Bearbeiten")}
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}
          {type !== "map" && (
            <Dropdown.Item as={Link} to={`/map?selectedTree=${selectedTreeId}`}>
              <MapMarkerAltIcon width={"16px"} /> {t("view_on_map", "Zur Karte")}
            </Dropdown.Item>
          )}
          {type === "map" && (
            <Dropdown.Item
              as={Link}
              to={
                pid
                  ? `/project/${pid}/list?selectedTree=${selectedTreeId}`
                  : `/trees?selectedTree=${selectedTreeId}`
              }
            >
              <MenuAlt1Icon width={"16px"} /> {t("view_on_list", "Zur Liste")}
            </Dropdown.Item>
          )}
          <Dropdown.Item as={Link} to={dsUrl}>
            <LineChartIcon width={"18px"} /> {t("view_on_chart", "Zu Data Science")}
          </Dropdown.Item>
          {sensors.length > 0 && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item as="div" className="px-2">
                <ExportSensor
                  name={tree?.name}
                  tree={tree}
                  sensors={sensors}
                  buttonClassName="btn btn-sm btn-outline-primary w-100"
                />
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  ) : null;

  return (
    <>
      <div
        className="treeDetail position-relative"
        style={{
          height: mobile ? "80vh" : "100%",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <div
          className="tree-detail-host entity-detail-panel"
          style={{
            background: "var(--color-bg-surface, white)",
            padding: "0",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            marginTop: 0,
            position: "relative",
          }}
        >
          <TreeDetailTabs
            tree={tree}
            treeImages={treeImages}
            leafImages={leafImages}
            treeIndex={treeIndex}
            setTreeIndex={setTreeIndex}
            leafIndex={leafIndex}
            setLeafIndex={setLeafIndex}
            handleDeleteImage={handleDeleteImage}
            setOpenImageForm={setOpenImageForm}
            edit={edit}
            setEdit={setEdit}
            soilEdit={soilEdit}
            setSoilEdit={setSoilEdit}
            selectedInstallation={selectedInstallation}
            setSelectedInstallation={setSelectedInstallation}
            showData={showData}
            setShowData={setShowData}
            onAddSensor={canEditTree ? handleAddSensor : undefined}
            onClose={showHeaderActions ? handleClose : null}
            headerActions={showHeaderActions ? headerActions : null}
            onSensorEditOpenChange={onSensorEditStateChange}
            canEditTree={canEditTree}
            canManage3DModel={canManage3DModel}
            onUpload3DModel={handleUpload3DModel}
            onRemove3DModel={handleRemove3DModel}
            is3DModelMutating={is3DModelMutating}
            loadCareData={loadCareData}
            showLocationMap={type !== "map"}
            isPublic={isPublic}
            onActiveTabChange={(tabKey) => {
              if (tabKey === "history" || tabKey === "tasks") {
                setLoadCareData(true);
              }
            }}
          />
        </div>
      </div>

      {openForm && (
        <TreeForm
          show={openForm}
          isUpdate={isUpdate}
          formValue={tree}
          setOpenForm={setOpenForm}
          setIsUpdate={setIsUpdate}
          initialStep={formInitialStep}
          isolatedStep={formIsolatedStep}
        />
      )}
      {openImageForm && (
        <TreeImageForm show={openImageForm} formValue={tree} setOpenForm={setOpenImageForm} />
      )}
    </>
  );
}

export const DropdownItem = ({ title, icon, onClick }) => {
  const { t } = useTranslation();
  return (
    <div>
      {t(title)}
      <Button onClick={onClick}>{icon}</Button>
    </div>
  );
};
